import React, { useState, useRef, useEffect } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '../hooks/useLanguage';
import { useTranslation } from '../hooks/useTranslation';
import { LanguageToggleProps } from '../types/i18n.types';

export const LanguageToggle: React.FC<LanguageToggleProps> = ({
  className = '',
  showText = true,
  size = 'md',
  variant = 'button',
  disabled = false
}) => {
  const {
    currentLanguage,
    changeLanguage,
    isChangingLanguage,
    supportedLanguages,
    toggleLanguage,
    getCurrentLanguageName,
    getCurrentLanguageFlag: _getCurrentLanguageFlag
  } = useLanguage();

  const { t } = useTranslation('header');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  // Get language flag icon — normalize locale codes (e.g. "en-US" → "en")
  const getLanguageIcon = (langCode: string) => {
    const normalized = langCode.split('-')[0].toLowerCase();
    return `${process.env.PUBLIC_URL}/icon/${normalized}.png`;
  };

  // Size-based styling
  const sizeClasses = {
    sm: {
      container: 'py-1.5 px-2 text-xs',
      icon: 'h-4 w-4',
      text: 'text-xs'
    },
    md: {
      container: 'py-1.5 px-3 text-sm',
      icon: 'h-5 w-5',
      text: 'text-sm'
    },
    lg: {
      container: 'py-1.5 px-4 text-base',
      icon: 'h-6 w-6',
      text: 'text-base'
    }
  };

  const currentSizeClasses = sizeClasses[size];

  // Base button styles
  const baseButtonStyles = `
    inline-flex items-center justify-center rounded-lg border border-gray-300
    bg-white text-gray-700 transition-all duration-150 hover:bg-gray-50
    focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2
    dark:border-border-dark dark:bg-dark-background-secondary dark:text-dark-text dark:hover:bg-dark-background-hover
    dark:focus:ring-gray-600
    disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white
    dark:disabled:hover:bg-dark-background-secondary
    ${currentSizeClasses.container} ${className}
  `;

  // Loading spinner
  const LoadingSpinner = () => (
    <div
      className={`animate-spin rounded-full border-2 border-current border-t-transparent ${currentSizeClasses.icon}`}
      aria-label={t('language.changing')}
    />
  );

  // Simple toggle button variant
  if (variant === 'button') {
    const handleToggle = async () => {
      if (disabled || isChangingLanguage) return;

      try {
        await toggleLanguage();
      } catch (error) {
        console.error('Failed to toggle language:', error);
      }
    };

    const nextLanguage = currentLanguage === 'vi' ? 'en' : 'vi';
    const _nextLanguageConfig = supportedLanguages.find(lang => lang.code === nextLanguage);

    return (
      <button
        onClick={handleToggle}
        disabled={disabled || isChangingLanguage}
        className={baseButtonStyles}
        title={t('tooltips.changeLanguage')}
        aria-label={`${t('language.change')} (${t(`tooltips.switchTo${nextLanguage === 'en' ? 'English' : 'Vietnamese'}`)})`}
      >
        {isChangingLanguage ? (
          <LoadingSpinner />
        ) : (
          <>
            <img
              src={getLanguageIcon(currentLanguage)}
              alt={currentLanguage}
              className={`${currentSizeClasses.icon} rounded-sm object-cover`}
            />
            {showText && (
              <span className={`ml-2 font-medium ${currentSizeClasses.text}`}>
                {currentLanguage.toUpperCase()}
              </span>
            )}
          </>
        )}
      </button>
    );
  }

  // Dropdown variant
  if (variant === 'dropdown') {
    const handleLanguageSelect = async (languageCode: string) => {
      if (disabled || isChangingLanguage || languageCode === currentLanguage) return;

      try {
        await changeLanguage(languageCode as any);
        setIsDropdownOpen(false);
      } catch (error) {
        console.error('Failed to change language:', error);
      }
    };

    return (
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => !disabled && !isChangingLanguage && setIsDropdownOpen(!isDropdownOpen)}
          disabled={disabled || isChangingLanguage}
          className={`${baseButtonStyles} ${isDropdownOpen ? 'ring-2 ring-gray-300 dark:ring-gray-600' : ''}`}
          aria-expanded={isDropdownOpen}
          aria-haspopup="true"
          title={t('language.current')}
        >
          {isChangingLanguage ? (
            <LoadingSpinner />
          ) : (
            <>
              <img
                src={getLanguageIcon(currentLanguage)}
                alt={currentLanguage}
                className={`${currentSizeClasses.icon} rounded-sm object-cover`}
              />
              {showText && (
                <>
                  <span className={`ml-2 font-medium ${currentSizeClasses.text}`}>
                    {getCurrentLanguageName(true)}
                  </span>
                  <ChevronDownIcon
                    className={`ml-1 h-3 w-3 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}
                  />
                </>
              )}
            </>
          )}
        </button>

        {/* Dropdown Menu */}
        {isDropdownOpen && !isChangingLanguage && (
          <div className="absolute right-0 mt-2 w-48 rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none dark:bg-dark-background-secondary dark:ring-gray-700 z-50">
            {supportedLanguages.map((language) => {
              const isActive = language.code === currentLanguage;

              return (
                <button
                  key={language.code}
                  onClick={() => handleLanguageSelect(language.code)}
                  disabled={isActive}
                  className={`
                    flex w-full items-center px-4 py-2 text-left text-sm transition-colors
                    ${isActive
                      ? 'bg-gray-100 text-gray-900 dark:bg-dark-background-tertiary dark:text-dark-text cursor-default'
                      : 'text-gray-700 hover:bg-gray-50 dark:text-dark-text dark:hover:bg-dark-background-hover'
                    }
                  `}
                  aria-current={isActive ? 'true' : undefined}
                >
                  <img
                    src={getLanguageIcon(language.code)}
                    alt={language.code}
                    className="mr-3 h-5 w-5 rounded-sm object-cover"
                  />
                  <span className="flex-1">{language.nativeName}</span>
                  {isActive && (
                    <span className="ml-2 text-xs text-gray-500 dark:text-dark-text-secondary">
                      {t('language.current')}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  return null;
};

export default LanguageToggle;